import React, {createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useState} from 'react';
import cloneDeep from "lodash/cloneDeep";
import {DataById} from "../redux/map/types";
import {usePresetsContext} from "./presetsContext";


type ContextType = {
    loading: boolean;
    setLoading: (loading: boolean) => void;
    enabledFilters: Record<string, any | any[]>;
    setEnabledFilters: (filters: Record<string, unknown>) => void;
    filters: any[];
    setFilters: (filters: DataById) => void;
    resetFilters: () => void;
    mapFilter: any[];
    setMapFilter: (arr: any[] | null) => void;
    layerPaint: Record<string, Record<string, unknown>>;
    setLayerPaint: Dispatch<SetStateAction<Record<string, Record<string, unknown>>>>;
};

const MapFiltersContext = createContext<ContextType>({} as ContextType);

const useMapFiltersContext = () => {
    return useContext(MapFiltersContext);
};

type ProviderProps = {
    children: React.ReactNode,
    loadEnabledFilters?: (presetId: string) => Promise<{ filters, enabledFilters }>,
    defaultFilters?: Record<string, any | any[]>,
}

const MapFiltersProvider = ({children, loadEnabledFilters, defaultFilters}: ProviderProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const {selectedPresetId} = usePresetsContext();
    const [enabledFilters, setEnabledFilters] = useState<any>(defaultFilters);
    const [defaultFiltersState, setDefaultFiltersState] = useState<any>([]);
    const [filters, setFilters] = useState<any>([]);
    const [mapFilter, setMapFilter] = useState<any>([]);
    const [layerPaint, setLayerPaint] = useState<Record<string, Record<string, unknown>>>({});

    useEffect(() => {
        if (selectedPresetId && loadEnabledFilters) {
            void (async function () {
                setLoading(true);
                const {enabledFilters, filters} = await loadEnabledFilters(selectedPresetId);
                setEnabledFilters(enabledFilters);
                setFilters(filters);
                setDefaultFiltersState(cloneDeep(filters));
                setLoading(false);
            }());
        }
    }, [selectedPresetId, loadEnabledFilters]);

    const resetFilters = useCallback(() => {
        setFilters(defaultFiltersState);
    }, [defaultFiltersState])

    const value = {
        loading,
        setLoading,
        enabledFilters,
        setEnabledFilters,
        filters,
        resetFilters,
        setFilters,
        mapFilter,
        setMapFilter,
        layerPaint,
        setLayerPaint
    }

    return (
        <MapFiltersContext.Provider value={value}>
            {children}
        </MapFiltersContext.Provider>
    );
};

export {MapFiltersProvider, useMapFiltersContext};